import React, { useEffect, useState } from "react";
import { Button, Form, Row, Col, Modal } from "react-bootstrap";
import { Formik, Field, Form as FormikForm, ErrorMessage } from "formik";
import { withTranslation } from "react-i18next";
import * as Yup from "yup";
import callApi from "../../helpers/CallApi";
import { createAdmin, getAdmin, updateAdmin } from "../../helpers/API";
import useApi from "../../Hooks/useApi";
import { toast } from "react-toastify";

const Add = ({ state, handleClose, refresh, t }) => {
  const { operation, id, show } = state;
  const [disabled, setDisabled] = useState(operation == "view");

  // Dynamic validation schema using translations
  const getValidationSchema = () => {
    return Yup.object().shape({
      name: Yup.string().required(t("admin.add.validation.nameRequired")),
      email: Yup.string()
        .email(t("admin.add.validation.emailInvalid"))
        .required(t("admin.add.validation.emailRequired")),
      mobile: Yup.string()
        .matches(/^[0-9]{10}$/, t("admin.add.validation.mobileInvalid"))
        .required(t("admin.add.validation.mobileRequired")),
    });
  };

  const initialValues = {
    name: "",
    email: "",
    mobile: "",
  };
  const [formData, setFormData] = useState(initialValues);

  useApi({
    callback: getAdmin,
    payload: id,
    dependencies: [id],
    shouldCallApi: Boolean(id),
    onSuccess: (res) => {
      const { name, email, mobile } = res;
      setFormData({
        name: name || "",
        email: email || "",
        mobile: String(mobile || ""),
      });
    },
  });

  useEffect(() => {
    setDisabled(operation === "view");
  }, [operation]);

  const handleSubmit = (values) => {
    const payload = {
      name: values.name,
      email: values.email,
      mobile: values.mobile,
    };

    callApi({
      callback: !id ? createAdmin : updateAdmin,
      payload: id ? [id, payload] : [payload],
      onSuccess: ({ message }) => {
        refresh((prev) => !prev);
        toast.success(
          message ||
            (!id
              ? t("admin.add.messages.createSuccess")
              : t("admin.add.messages.updateSuccess"))
        );
        handleClose();
      },
      onError: () => {
        toast.error(t("admin.add.messages.error"));
      },
    });
  };

  const getHeading = () => {
    return t(`admin.add.headings.${operation}`);
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      size="lg"
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton>
        <Modal.Title>{getHeading()}</Modal.Title>
      </Modal.Header>

      <Formik
        initialValues={formData}
        enableReinitialize
        validationSchema={getValidationSchema()}
        onSubmit={handleSubmit}
        validateOnBlur
        validateOnChange
      >
        {({ errors, touched }) => (
          <FormikForm>
            <Modal.Body>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="required">
                      {t("admin.add.labels.name")}
                    </Form.Label>
                    <Field
                      name="name"
                      disabled={disabled}
                      className={`form-control ${
                        touched.name && errors.name ? "is-invalid" : ""
                      }`}
                      placeholder={t("admin.add.placeholders.enterName")}
                    />
                    <ErrorMessage
                      name="name"
                      component="div"
                      className="invalid-feedback"
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="required">
                      {t("admin.add.labels.email")}
                    </Form.Label>
                    <Field
                      name="email"
                      type="email"
                      disabled={disabled}
                      className={`form-control ${
                        touched.email && errors.email ? "is-invalid" : ""
                      }`}
                      placeholder={t("admin.add.placeholders.enterEmail")}
                    />
                    <ErrorMessage
                      name="email"
                      component="div"
                      className="invalid-feedback"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-1">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="required">
                      {t("admin.add.labels.mobileNumber")}
                    </Form.Label>
                    <Field
                      name="mobile"
                      disabled={disabled}
                      className={`form-control ${
                        touched.mobile && errors.mobile ? "is-invalid" : ""
                      }`}
                      placeholder={t("admin.add.placeholders.enterMobile")}
                    />
                    <ErrorMessage
                      name="mobile"
                      component="div"
                      className="invalid-feedback"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Modal.Body>

            <Modal.Footer>
              <Button variant="secondary" onClick={handleClose}>
                {disabled
                  ? t("admin.add.buttons.close")
                  : t("admin.add.buttons.cancel")}
              </Button>
              {!disabled && (
                <Button variant="primary" type="submit">
                  {t("admin.add.buttons.save")}
                </Button>
              )}
            </Modal.Footer>
          </FormikForm>
        )}
      </Formik>
    </Modal>
  );
};

export default withTranslation()(Add);
